import { Injectable } from '@angular/core';

import { FlexTimeActivityInstance } from 'libs/domain';
import { flatten } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ListMembershipService } from '@app/src/app/services/ListMembership';

import { OptionItem } from '@shared/components/form/components/form-grouped-select/form-grouped-select.types';
import { UserListCategory } from '@shared/components/user-list-filter';
import { CacheService } from '@shared/services/cache/cache.service';
import { CacheKey, FetchOptions } from '@shared/services/cache/cache.types';
import { createCacheCollection } from '@shared/services/cache/cache.utils';
import { FlexTimeRegistrationService } from '@shared/services/flex-time';

import { Student } from '../types/tt-my-class.types';
import { mapPersonToStudent } from '../utils/my-class.utils';

@Injectable()
export class MyClassLists {
  private _listFetchers: Record<UserListCategory, (list: OptionItem, opts: FetchOptions) => Observable<Student[]>> = {
    [UserListCategory.ALL]: (list, opts) => this._getMembersForList(list, opts),
    [UserListCategory.ALL_CURRENT_TERM]: (list, opts) => this._getMembersForList(list, opts),
    [UserListCategory.MY_LISTS]: (list, opts) => this._getMembersForList(list, opts),
    [UserListCategory.MY_LISTS_CURRENT_TERM]: (list, opts) => this._getMembersForList(list, opts),
    [UserListCategory.TODAYS_FLEX_ACTIVITIES]: (list, opts) => this._getMembersForFlexActivity(list, opts),
    [UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE]: (list, opts) => this._getMembersForFlexActivity(list, opts),
    [UserListCategory.HOMEROOM]: (list, opts) => this._getMembersForList(list, opts),
    [UserListCategory.HOMEROOM_MINE]: (list, opts) => this._getMembersForList(list, opts),
  };

  private _cachedLists = createCacheCollection(key =>
    this._cacheService.create<Student[]>(
      `${CacheKey.MY_CLASS_LIST_MEMBERS}:${key}`,
      async listId => {
        const users = await this._listService.getMembersOfList(listId);
        return users.map(u => {
          const person = { ...u, listTypeOrigin: 'userlist' };
          return mapPersonToStudent(person);
        });
      },
      {
        ttl: 60,
      },
    ),
  );

  private _cachedFlexLists = createCacheCollection(key =>
    this._cacheService.create<Student[]>(
      `${CacheKey.MY_CLASS_FLEX_LIST_MEMBERS}:${key}`,
      async (instance: FlexTimeActivityInstance) => {
        const registrants = await this._ftRegistration.fetchRegisteredPeople(instance.id, {
          checkinReasonId: instance.checkinReasonId,
        });

        return registrants.map(r => {
          const flexTimeActivity = {
            instance,
            status: r.checkInStatus,
            checkedInAt: r.checkedInAt,
          };

          const person = {
            ...r,
            listTypeOrigin: 'flextime',
            flexTimeActivity,
          };
          return mapPersonToStudent(person);
        });
      },
      {
        ttl: 15,
      },
    ),
  );

  constructor(
    private _cacheService: CacheService,
    private _listService: ListMembershipService,
    private _ftRegistration: FlexTimeRegistrationService,
  ) {}

  public fetchMembersForLists(lists: OptionItem[], opts?: FetchOptions): Observable<Student[]> {
    if (!lists || lists.length === 0) {
      return of([]);
    }
    // we need to process flex lists first so that flex data takes priority over user lists for students in both lists
    const sortedLists = [...lists].sort((a, b) => {
      if (a.category.value === UserListCategory.TODAYS_FLEX_ACTIVITIES || a.category.value === UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE) return -1;
      if (b.category.value === UserListCategory.TODAYS_FLEX_ACTIVITIES || b.category.value === UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE) return 1;
      return 0;
    });

    return forkJoin(
      sortedLists
        .filter(list => this._listFetchers[list.category.value])
        .map(list => this._listFetchers[list.category.value](list, opts) as Observable<Student[]>),
    ).pipe(switchMap(results => of(flatten(results))));
  }

  private _getMembersForList(list: OptionItem, opts?: FetchOptions): Observable<Student[]> {
    const listId = list.value;
    const cacheItem = this._cachedLists.get(listId);
    return cacheItem.get(listId, opts);
  }

  private _getMembersForFlexActivity(list: OptionItem, opts?: FetchOptions): Observable<Student[]> {
    const activity = list.data;
    const cacheItem = this._cachedFlexLists.get(activity.id);
    return cacheItem.get(activity, opts);
  }
}
